import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material';
import { ActivatedRoute, NavigationExtras, Params, Router } from '@angular/router';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { Company } from '@core/interfaces';
import { MaintenanceData } from '@core/interfaces/maintenances/index';
import { endOfDay, mapIds, startOfDay } from '@core/utils/filters-utils';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { CompanyService } from 'app/main/settings/accounts/companies/services/company.service';
import { isEmpty, isEqual } from 'lodash';
import { Subscription } from 'rxjs';
import { debounceTime, filter, switchMap, tap } from 'rxjs/operators';
import { sentry } from 'sentry';
import { ReportsService } from '../reports.service';
import { FilterComponent } from './components/filter/filter.component';
import { DialogChooseCompaniesComponent } from './dialogs/dialog-choose-companies/dialog-choose-companies.component';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';
import { CompanyToCompare } from './interface';
import { DataTableMapperService } from './services/data-table.mapper.service';

@Component({
  selector: 'app-company-comparison',
  templateUrl: './company-comparison.component.html',
  styleUrls: ['./company-comparison.component.scss']
})
export class CompanyComparisonComponent implements OnInit, OnDestroy {

  @ViewChild(FilterComponent, { static: true })
  public filter: FilterComponent;
  public isLoading = false;
  public isLoadingAllData = false;
  public isFilterOn = false;
  public selectedCompanies: Company[] = [];
  public selectedCompaniesWithTableData: CompanyToCompare[] = [];
  public selectedCompaniesWithTableDataObjectStructureExample: CompanyToCompare;
  public filterSubscription: Subscription;

  private autoReloadTime = 2 * 60000;
  private intervalID: NodeJS.Timer;
  private routeName = this.route.snapshot.routeConfig.path;

  public shouldShowStars: String[] = ['totalAverage'];
  public shouldDisplayTooltip: String[] = ['timeAverageTotal', 'donePercentage', 'notDonePercentage'];
  public shouldInFormatDurationDays: String[] = ['timeAverageWithDeadline'];
  public shouldInFormatDurationSeconds: String[] = ['pending', 'requested', 'onNegotiation', 'scheduled', 'inProgress', 'reported', 'outOfDeadline', 'timeAverageTotal'];
  public isPercentage: String[] = ['notDonePercentage', 'donePercentage', 'acceptedPercentage', 'refusedPercentage', 'inDeadLinePercentage'];

  public skeletonColumnsQuantityWhenAlreadySomeDataLoaded: number[] = [];
  public skeletonColumnsQuantityWhenNoDataLoaded: number[] = [];
  public skeletonRowsQuantity: number[] = [0, 1, 2, 3, 4, 5];

  constructor(
    private router: Router,
    private matDialog: MatDialog,
    private route: ActivatedRoute,
    private reportsService: ReportsService,
    private companyService: CompanyService,
    private authenticationService: AuthenticationService,
    private dataTableMapperService: DataTableMapperService,
    private fuseProgressBarService: FuseProgressBarService,
    private fuseTranslationLoaderService: FuseTranslationLoaderService,
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
  }

  ngOnInit(): void {
    this.getSavedData().then(() => {
      this.updateData();
    });
    this.filterSubscription = this.filter.filtersForm.valueChanges
      .pipe(
        filter(() => this.filter.filtersForm.valid),
        tap(() => this.isLoadingAllData = true),
        tap(() => this.skeletonColumnsQuantityWhenAlreadySomeDataLoaded = Array.from({ length: this.selectedCompanies.length }, (_, index) => index + 1)),
        debounceTime(1000),
        tap(() => this.selectedCompaniesWithTableData = []),
        tap(() => {
          const currentWhere = this.toQuery(this.filter.filtersForm.value);
          const defaultWhere = this.toQuery(this.filter.defaultFilterValues);
          this.isFilterOn = !isEqual(currentWhere, defaultWhere);
        }),
        switchMap(() => this.updateAllData())
      ).subscribe(() => {
        this.isLoadingAllData = false;
        this.setDataToQueryParams(this.selectedCompanies);
      });
  }

  ngOnDestroy(): void {
    if (this.filterSubscription) {
      this.filterSubscription.unsubscribe();
    }

    if (this.filter.filtersForm.invalid) {
      return;
    }

    this.setDataOnLocalStorage();
  }

  openChooseCompaniesDialog(): void {
    const chooseCompaniesDialog = this.matDialog.open(DialogChooseCompaniesComponent, {
      panelClass: ['field-dialog-860'],
      data: {
        selectedCompanies: this.selectedCompanies
      }
    });

    chooseCompaniesDialog.afterClosed().subscribe(async (data) => {
      const hasData = data && data.data;
      if (!hasData) {
        return;
      }

      this.selectedCompanies = data.data.selectedCompanies;
      this.setDataToQueryParams(this.selectedCompanies);
      this.updateData();
    });
  }

  private async updateData(): Promise<void> {
    const maintenanceDataPromises: Promise<CompanyToCompare>[] = [];

    this.isLoading = true;
    this.fuseProgressBarService.show();

    for (const company of this.selectedCompanies) {
      const companyAlreadySelected = !!(this.selectedCompaniesWithTableData.filter((alreadySelectedCompany) => {
        return alreadySelectedCompany.companyId === company.id;
      }).length);

      if (companyAlreadySelected) {
        continue;
      }

      maintenanceDataPromises.push(this.getCompanyMaintenanceData(company));
    }

    this.skeletonColumnsQuantityWhenNoDataLoaded = Array.from({ length: this.selectedCompanies.length + 1 }, (_, index) => index + 1);
    this.skeletonColumnsQuantityWhenAlreadySomeDataLoaded = Array.from({ length: maintenanceDataPromises.length }, (_, index) => index + 1);

    const shouldPreserveSelectedCompanies = this.selectedCompaniesWithTableData.length && maintenanceDataPromises.length;
    const shouldRemoveUnselectedCompanies = !maintenanceDataPromises.length;

    if (shouldPreserveSelectedCompanies) {
      this.selectedCompaniesWithTableData = this.removeUnselectedCompanies(this.selectedCompaniesWithTableData, this.selectedCompanies);
      this.selectedCompaniesWithTableData.push(...await Promise.all(maintenanceDataPromises));
    } else if (shouldRemoveUnselectedCompanies) {
      this.selectedCompaniesWithTableData = this.removeUnselectedCompanies(this.selectedCompaniesWithTableData, this.selectedCompanies);
    } else {
      this.selectedCompaniesWithTableData = await Promise.all(maintenanceDataPromises);
    }

    this.selectedCompaniesWithTableDataObjectStructureExample = this.selectedCompaniesWithTableData[0];
    this.isLoading = false;
    this.fuseProgressBarService.hide();
  }

  private removeUnselectedCompanies(alreadySelectedCompaniesData, newSelectedCompanies): CompanyToCompare[] {
    return alreadySelectedCompaniesData.filter((selectedCompanyData) =>
      newSelectedCompanies.some((selectedCompany) => selectedCompany.id === selectedCompanyData.companyId)
    );
  }

  private async updateAllData(): Promise<void> {
    const maintenanceDataPromises: Promise<CompanyToCompare>[] = [];

    for (const company of this.selectedCompanies) {
      maintenanceDataPromises.push(this.getCompanyMaintenanceData(company));
    }

    this.selectedCompaniesWithTableData = await Promise.all(maintenanceDataPromises);
  }

  private async getCompanyMaintenanceData(company: Company): Promise<CompanyToCompare> {
    try {
      const where = this.toQuery({ ...this.filter.filtersForm.value, companyId: company.id });

      const [
        accountMaintenancesStatus,
        accountMaintenancesStatusInterval,
        accountMaintenancesVolumeCount,
        accountRatingsCount,
        averageDeadlines
      ] = await Promise.all([
        this.reportsService.accountMaintenancesStatus(where),
        this.reportsService.accountMaintenancesStatusInterval(where),
        this.reportsService.accountMaintenancesVolumeCount(where),
        this.reportsService.getAccountRatingsCount(where),
        this.reportsService.getAverageDeadlines(where).toPromise(),
      ]);

      const maintenanceData: MaintenanceData = {
        companyId: company.id,
        name: company.name,
        avatarUrl: company.avatarUrl,
        status: accountMaintenancesStatus.status,
        totalMaintenance: accountMaintenancesStatus.totalMaintenances,
        statusInterval: accountMaintenancesStatusInterval,
        volumeCount: accountMaintenancesVolumeCount,
        ratingsCount: accountRatingsCount,
        averageDeadlines: averageDeadlines
      };

      return this.dataTableMapperService.convertMaintenanceDataToDataTable(maintenanceData);
    } catch (err) {
      sentry.captureException(err);
    }
  }

  private toQuery(value: any): Object {
    return {
      locationGroupId_in: mapIds(value.locationGroups),
      locationId_in: mapIds(value.locations),
      typeId_in: mapIds(value.maintenanceTypes),
      segmentId_in: mapIds(value.segments),
      companyId_in: value.companyId,
      openedAt_gte: startOfDay(value.openedAtGte),
      openedAt_lte: endOfDay(value.openedAtLte),
      scheduledAt_gte: startOfDay(value.scheduledAtGte, 'YYYY-MM-DD'),
      scheduledAt_lte: endOfDay(value.scheduledAtLte, 'YYYY-MM-DD'),
      closedAt_gte: startOfDay(value.closedAtGte),
      closedAt_lte: endOfDay(value.closedAtLte),
    };
  }

  private setDataOnLocalStorage(): void {
    if (this.authenticationService && this.authenticationService.user && this.authenticationService.user.id && this.filter.filtersForm.valid) {
      const userId = this.authenticationService.user.id;
      const hasSavedData = !!(this.selectedCompanies.length || this.isFilterOn);
      const data = hasSavedData ? {
        userId: userId,
        companies: this.selectedCompanies.length ? JSON.stringify(mapIds(this.selectedCompanies)) : null,
        filters: this.isFilterOn ? this.filter.filtersForm.value : null
      } : null;

      localStorage.setItem(this.routeName, JSON.stringify(data));
    }
  }

  private setDataToQueryParams(companies: Company[]): void {
    if (this.filter.filtersForm.invalid) {
      return;
    }

    const userId = this.authenticationService.user.id;
    const hasSavedData = !!(this.selectedCompanies.length || this.isFilterOn);
    const navigationQuery: NavigationExtras = hasSavedData ? {
      queryParams: {
        userId: userId,
        companies: companies.length ? JSON.stringify(mapIds(companies)) : null,
        filters: this.isFilterOn ? JSON.stringify(this.filter.filtersForm.value) : null
      },
      queryParamsHandling: 'merge'
    } : null;

    const url = navigationQuery ? this.router.serializeUrl(
      this.router.createUrlTree([this.routeName], { queryParams: { ...navigationQuery.queryParams } })
    ) : this.routeName;
    this.router.navigateByUrl(url);
  }

  public formatPercentageData(property: string, tableName: string, company: CompanyToCompare): string {
    const value = company.table[tableName][property];
    if (property === 'inDeadLinePercentage') {
      return (value * 100) + '%';
    }
    return (value % 1 === 0) ? value.toFixed(0) + '%' : value.toFixed(1) + '%';
  }

  private async getSavedData(): Promise<void> {
    const dataFromUrl = this.route.snapshot.queryParams;

    if (isEmpty(dataFromUrl)) {
      const dataFromLocalStorage = JSON.parse(localStorage.getItem(this.routeName));
      const isUserValidLocalStorage = dataFromLocalStorage && dataFromLocalStorage.userId === this.authenticationService.user.id;
      const savedCompaniesLocalStorage = dataFromLocalStorage && dataFromLocalStorage.companies;
      const savedFiltersLocalStorage = dataFromLocalStorage && dataFromLocalStorage.filters;

      if (!isUserValidLocalStorage) {
        return;
      }

      if (savedCompaniesLocalStorage) {
        const companies = [];
        const companyIds = JSON.parse(savedCompaniesLocalStorage);

        for (const companyId of companyIds) {
          companies.push(await this.companyService.getCompanyById(companyId));
        }
        this.selectedCompanies = companies;
      }

      if (savedFiltersLocalStorage) {
        this.filter.filtersForm.patchValue(savedFiltersLocalStorage);
        this.isFilterOn = true;
      }

      this.setDataToQueryParams(this.selectedCompanies);
      return;
    }

    const isUserValidUrl = dataFromUrl.userId === this.authenticationService.user.id;
    const companiesFromUrl = dataFromUrl.companies ? JSON.parse(dataFromUrl.companies) : null;
    const filtersFromUrl = dataFromUrl.filters ? JSON.parse(dataFromUrl.filters) : null;

    if (!isUserValidUrl) {
      return;
    }

    if (companiesFromUrl) {
      const company = [];
      for (const companyId of companiesFromUrl) {
        company.push(await this.companyService.getCompanyById(companyId));
      }
      this.selectedCompanies = company;
    }

    if (filtersFromUrl) {
      this.filter.filtersForm.patchValue(filtersFromUrl);
      this.isFilterOn = true;
    }
  }

  removeCompany(id: string): void {
    this.selectedCompanies = this.selectedCompanies.filter((selectedCompany) => selectedCompany.id !== id);
    this.selectedCompaniesWithTableData = this.selectedCompaniesWithTableData.filter((selectedCompany) => selectedCompany.companyId !== id);

    const queryParams = this.router.routerState.snapshot.root.queryParams;
    const companies = queryParams.companies ? JSON.parse(queryParams.companies) : null;

    if (!companies) {
      return;
    }

    const filteredCompanies = companies.filter((company) => company.id !== id);
    this.setDataToQueryParams(filteredCompanies);
  }

  reload(): void {
    this.filter.filtersForm.updateValueAndValidity();
  }

  autoRefresh(event: { checked: boolean }): void {
    if (event.checked) {
      this.intervalID = setInterval(() => this.reload(), this.autoReloadTime);
      return;
    }

    clearInterval(this.intervalID);
  }

  clearFilters(): void {
    const queryParams = this.router.routerState.snapshot.root.queryParams;
    const shouldClearAllQueryParams = !this.selectedCompanies.length;
    let newQueryParams: Object = {};

    if (shouldClearAllQueryParams) {
      const { filters, userId, ...restQueryParams } = queryParams;
      newQueryParams = restQueryParams;
    } else {
      const { filters, ...restQueryParams } = queryParams;
      newQueryParams = restQueryParams;
    }

    const url = this.router.serializeUrl(this.router.createUrlTree([this.routeName], { queryParams: { ...newQueryParams } }));
    this.filter.filtersForm.patchValue(this.filter.defaultFilterValues);
    this.isFilterOn = false;
    this.setDataOnLocalStorage();

    this.router.navigateByUrl(url);
  }

  clearCompanies(): void {
    const queryParams = this.router.routerState.snapshot.root.queryParams;
    const shouldClearAllQueryParams = !this.isFilterOn;
    let newQueryParams: Object = {};

    if (shouldClearAllQueryParams) {
      const { companies, userId, ...restQueryParams } = queryParams;
      newQueryParams = restQueryParams;
    } else {
      const { companies, ...restQueryParams } = queryParams;
      newQueryParams = restQueryParams;
    }

    const url = this.router.serializeUrl(this.router.createUrlTree([this.routeName], { queryParams: { ...newQueryParams } }));
    this.selectedCompanies = [];
    this.selectedCompaniesWithTableData = [];
    this.setDataOnLocalStorage();

    this.router.navigateByUrl(url);
  }

  getObjectKeys(obj: any): string[] {
    if (!obj) {
      return [];
    }

    return Object.keys(obj);
  }
}
